import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Sparkles, Clock, Heart, Check, Phone, Mail, MapPin, MessageCircle, ShoppingBag, UserPlus, FileText, CalendarCheck } from "lucide-react";
import { getPublicLanding, trackLanding } from "@/lib/api";

const ICONS = {
  sparkles: Sparkles,
  clock: Clock,
  heart: Heart,
  "calendar-check": CalendarCheck,
  "user-plus": UserPlus,
  mail: Mail,
  "file-text": FileText,
  "shopping-bag": ShoppingBag,
  "message-circle": MessageCircle,
  phone: Phone,
};

function useSessionId() {
  const ref = useRef(null);
  if (!ref.current) {
    let sid = sessionStorage.getItem("nadia-sid");
    if (!sid) {
      sid = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem("nadia-sid", sid);
    }
    ref.current = sid;
  }
  return ref.current;
}

export default function PublicLanding() {
  const { slug } = useParams();
  const session_id = useSessionId();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const trackedVisit = useRef(false);

  useEffect(() => {
    getPublicLanding(slug)
      .then((d) => {
        setData(d);
        if (!trackedVisit.current) {
          trackedVisit.current = true;
          trackLanding({
            slug: d.landing.slug,
            variant: d.landing.variant,
            type: "visit",
            session_id,
          }).catch(() => {});
        }
      })
      .catch(() => setNotFound(true));
  }, [slug, session_id]);

  const track = (type, metadata) => {
    if (!data) return;
    trackLanding({
      slug: data.landing.slug,
      variant: data.landing.variant,
      type,
      session_id,
      metadata: metadata || {},
    }).catch(() => {});
  };

  if (notFound)
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-neutral-900 p-8 text-center">
        <div>
          <h1 className="text-2xl font-heading font-bold mb-2">Offerta non disponibile</h1>
          <p className="text-neutral-500">Questa pagina non è ancora pubblicata.</p>
        </div>
      </div>
    );
  if (!data) return <div className="min-h-screen bg-white" />;

  const l = data.landing;
  const biz = data.business;
  const brand = l.brand || {};
  const primary = brand.primary || "#0F172A";
  const accent = brand.accent || "#0EA5E9";
  const font = brand.font || "'Outfit', system-ui, sans-serif";
  const hero = l.gallery?.[0];
  const cfg = l.conversion_config || { type: "booking" };

  const handleCta = (e) => {
    track("cta_click");
    // For non-form conversions, route to external + record conversion
    if (cfg.type === "whatsapp" && cfg.whatsapp_phone) {
      e.preventDefault();
      track("conversion", { channel: "whatsapp" });
      const phone = cfg.whatsapp_phone.replace(/\D/g, "");
      const text = encodeURIComponent(`Ciao! Vorrei informazioni su "${l.offer_title}"`);
      window.open(`https://wa.me/${phone}?text=${text}`, "_blank");
    } else if (cfg.type === "call" && cfg.tel_number) {
      track("conversion", { channel: "call" });
      window.location.href = `tel:${cfg.tel_number}`;
    } else if (cfg.type === "purchase" && cfg.external_url) {
      e.preventDefault();
      track("conversion", { channel: "external_purchase" });
      window.open(cfg.external_url, "_blank");
    }
  };

  const submitForm = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = Object.fromEntries(fd.entries());
    track("form_submit", payload);
    track("conversion", { channel: "form", ...payload });
    setSubmitted(true);
  };

  const showForm = ["booking", "registration", "lead", "quote"].includes(cfg.type);
  const CtaTag = showForm ? "a" : "button";

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-neutral-900" style={{ fontFamily: font }} data-testid="public-landing">
      {/* Variant badge for debugging in preview mode */}
      {l.variant === "B" && (
        <div className="fixed top-3 right-3 z-20 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-white shadow-lg" style={{ background: accent }}>
          Variante B
        </div>
      )}
      {/* Top bar */}
      <div className="border-b border-neutral-200/70 bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: primary }}>
              {biz.name?.charAt(0) || "N"}
            </span>
            <span className="font-semibold tracking-tight">{biz.name}</span>
          </div>
          <a href="#cta" onClick={handleCta} className="px-4 py-2 rounded-full text-white text-sm font-semibold" style={{ background: primary }}>
            {l.cta_label}
          </a>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 pt-14 pb-10 sm:pt-20 sm:pb-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest" style={{ background: `${accent}18`, color: primary, border: `1px solid ${accent}55` }}>
              <Sparkles size={11} /> {l.offer_title}
            </div>
            <h1 className="mt-5 font-heading font-black leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl">{l.hero_title}</h1>
            <p className="mt-5 text-lg text-neutral-600 max-w-xl leading-relaxed">{l.hero_subtitle}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <CtaTag
                href="#cta"
                onClick={handleCta}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold shadow-lg transition-transform hover:-translate-y-0.5 cursor-pointer"
                style={{ background: primary }}
                data-testid="hero-cta"
              >
                {l.cta_label}
              </CtaTag>
              <span className="text-sm text-neutral-500">{l.urgency_line}</span>
            </div>
          </div>
          {hero && (
            <div className="relative">
              <div className="absolute -inset-6 rounded-[36px] blur-3xl opacity-30" style={{ background: accent }} />
              <img src={hero} alt="" className="relative w-full h-[420px] object-cover rounded-[28px] shadow-2xl" />
            </div>
          )}
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-5xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {(l.benefits || []).map((b, i) => {
          const Ic = ICONS[b.icon] || Sparkles;
          return (
            <div key={i} className="rounded-3xl bg-white p-6 border border-neutral-200/80 shadow-sm" data-testid={`benefit-${i}`}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: `${accent}18`, color: primary }}>
                <Ic size={20} />
              </div>
              <div className="font-heading font-bold text-lg tracking-tight">{b.title}</div>
              <p className="mt-1.5 text-neutral-600 text-sm leading-relaxed">{b.text}</p>
            </div>
          );
        })}
      </section>

      {/* Offer + CTA */}
      <section id="cta" className="max-w-5xl mx-auto px-6 pb-14">
        <div className="rounded-[32px] p-8 sm:p-12 text-white relative overflow-hidden" style={{ background: primary }}>
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl opacity-30" style={{ background: accent }} />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ background: `${accent}30`, color: "#fff" }}>
                Offerta {l.offer_title}
              </div>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-black leading-tight">{l.offer_description}</h2>
              <p className="mt-4 text-white/70 text-sm">{l.offer_terms}</p>
            </div>

            {showForm ? (
              <form onSubmit={submitForm} className="rounded-2xl bg-white text-neutral-900 p-6 shadow-2xl" data-testid="landing-form">
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: `${accent}22`, color: primary }}>
                      <Check size={26} />
                    </div>
                    <div className="font-heading text-xl font-bold">Grazie!</div>
                    <p className="text-neutral-500 text-sm mt-1">Ti confermiamo a breve via email.</p>
                  </div>
                ) : (
                  <>
                    <div className="font-heading font-bold text-lg mb-4">Compila il modulo</div>
                    <div className="space-y-3">
                      {(cfg.form_fields || ["name", "email"]).map((f) => (
                        <input
                          key={f}
                          name={f}
                          required={["name", "email"].includes(f)}
                          type={f === "email" ? "email" : f === "date" ? "date" : "text"}
                          placeholder={FIELD_LABELS[f] || f}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-neutral-900 outline-none text-sm"
                        />
                      ))}
                      <button type="submit" className="w-full py-3 rounded-xl font-semibold text-white" style={{ background: primary }} data-testid="landing-submit">
                        {l.cta_label}
                      </button>
                      <p className="text-[11px] text-neutral-400 text-center">Nessuno spam. Ti rispondiamo in pochi minuti.</p>
                    </div>
                  </>
                )}
              </form>
            ) : (
              <div className="rounded-2xl bg-white text-neutral-900 p-8 shadow-2xl text-center">
                <div className="w-14 h-14 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: `${accent}22`, color: primary }}>
                  {cfg.type === "whatsapp" ? <MessageCircle size={26} /> : cfg.type === "call" ? <Phone size={26} /> : <ShoppingBag size={26} />}
                </div>
                <div className="font-heading text-xl font-bold mb-1">
                  {cfg.type === "whatsapp" && "Chattiamo su WhatsApp"}
                  {cfg.type === "call" && "Chiamaci subito"}
                  {cfg.type === "purchase" && "Acquista direttamente online"}
                </div>
                <p className="text-neutral-500 text-sm mb-6">Un tap, un contatto immediato.</p>
                <button onClick={handleCta} className="w-full py-3 rounded-xl font-semibold text-white" style={{ background: primary }} data-testid="landing-cta-external">
                  {l.cta_label}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {l.gallery && l.gallery.length > 1 && (
        <section className="max-w-5xl mx-auto px-6 pb-14">
          <h3 className="font-heading text-2xl font-bold tracking-tight mb-6">Il posto in cui ti aspettiamo</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {l.gallery.slice(1).map((g, i) => (
              <img key={i} src={g} alt="" className="w-full h-40 sm:h-56 object-cover rounded-2xl" />
            ))}
          </div>
        </section>
      )}

      {/* Contact */}
      <footer className="border-t border-neutral-200/80 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-10 grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}22`, color: primary }}>
              <Phone size={16} />
            </span>
            <div>
              <div className="text-neutral-500 text-xs uppercase tracking-wider">Telefono</div>
              <div className="font-medium">{l.contact?.phone}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}22`, color: primary }}>
              <Mail size={16} />
            </span>
            <div>
              <div className="text-neutral-500 text-xs uppercase tracking-wider">Email</div>
              <div className="font-medium">{l.contact?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}22`, color: primary }}>
              <MapPin size={16} />
            </span>
            <div>
              <div className="text-neutral-500 text-xs uppercase tracking-wider">Dove siamo</div>
              <div className="font-medium">{l.contact?.address}</div>
            </div>
          </div>
        </div>
        <div className="text-center text-xs text-neutral-400 pb-6">{biz.name} · pagina generata da Nadia Cloud</div>
      </footer>
    </div>
  );
}

const FIELD_LABELS = {
  name: "Nome e cognome",
  email: "Email",
  phone: "Telefono",
  people: "Numero di persone",
  date: "Data",
  message: "Il tuo messaggio",
  project: "Descrivi brevemente il progetto",
};
